import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Grid,
  Box,
  Select,
  MenuItem,
  FormControl, TextField
} from '@mui/material';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import styles from '../ConfirmDeletionModal/styles.module.scss';
import '../ConfirmDeletionModal/styles.module.scss';
import { isFunc } from '@utils/functions';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import { bookMeeting, getAvailableTimeSlots } from '@user/sagas';
import { displayErrorMessage } from '@utils/displayErrorMessage';
import { MONTHS, WEEK_DAYS } from '@src/vars';
import history from '@utils/history';
import { getTimeZone } from '@utils/helpers';
import SearchIcon from "@mui/icons-material/Search";


const TIMEZONE = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;
const TIMEZONE_OFFSET = `GMT${getTimeZone()}`;
const REASON_MODE = 'reason';
const TIME_MODE = 'time';

const ScheduleMeetingModal = ({
  open,
  setOpen,
  specialistUser,
  yesAction,
  noAction,
  eventType,
  reason
}) => {
  const dispatch = useDispatch();
  const dimensions = useSelector(state => state.dimensions);
  const { user, selectedUser } = useSelector(state => state.user);

  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeUser, setActiveUser] = useState(undefined);
  const [reasonToCall, setReasonToCall] = useState('');
  const [mode, setMode] = useState(REASON_MODE);//reason|time
  const [isAlreadyClicked, setIsAlreadyClicked] = useState(false);

  useEffect(() => {
    if (open) setIsProcessing(false);
  }, [open]);//eslint-disable-line

  useEffect(() => {
    setActiveUser(selectedUser ?? user);
  }, [user, selectedUser]); // eslint-disable-line

  useEffect(() => {
    setReasonToCall(reason);
  }, [reason]); // eslint-disable-line

  useEffect(() => {
    if (eventType && !!specialistUser?.id && open) {
      setIsProcessing(true);
      dispatch(getAvailableTimeSlots({
        eventType,
        specialistId: specialistUser.id,
        onSuccess: (data) => {
          let rawDates = (data.availableSlots ?? []).map((dateString) => {
            const date = new Date(dateString);
            const weekDay = WEEK_DAYS[date.getDay()];
            const day = date.getDate();
            const month = MONTHS[date.getMonth()];
            const year = date.getFullYear();

            return {
              dateString: dateString,
              year,
              month,
              day,
              weekDay,
              hour: date.getHours(),
              minute: date.getMinutes(),
              dayString: `${weekDay}, ${day} ${month} ${year}`
            };
          });

          let groupedDates = [];
          rawDates.forEach((d) => {
            const dateIndex = groupedDates.findIndex((gd) => gd.dayString === d.dayString);
            const newTimeSlot = { buttonLabel: getFormattedTimeLabel(d.hour, d.minute), dateString: d.dateString };
            if (dateIndex < 0) {
              groupedDates.push({ dayString: d.dayString, timeSlots: [newTimeSlot] });
            } else {
              groupedDates[dateIndex].timeSlots.push(newTimeSlot);
            }
          });

          setDates(groupedDates);
          setSelectedDate(null);
          setSelectedTime(null);
          setIsProcessing(false);
        },
        onError: (data) => {
          displayErrorMessage(data, 'Unable to get available time slots');
          setIsProcessing(false);
        }
      }))
    } else if (!open) {
      setDates([]);
      setSelectedDate(null);
      setSelectedTime(null);
    }
  }, [eventType, specialistUser, open]); // eslint-disable-line

  const getFormattedTimeLabel = (hours, minutes) => {
    const postfix = (hours < 12 ? 'AM' : 'PM');
    const hourNumber = hours === 0 ? 12 : (hours <= 12 ? hours : hours - 12);
    return `${hourNumber < 10 ? `0${hourNumber}` : hourNumber}:${minutes < 10 ? `0${minutes}` : minutes} ${postfix}`;
  };

  const renderDateSelector = () => {
    return (<FormControl variant="outlined" className={styles.selectWrapper}>
      <Select value={selectedDate} displayEmpty style={{ backgroundColor: "white" }} disabled={isProcessing} SelectDisplayProps={{ 'data-testid': 'select-schedule-meeting-date' }}  >
        <MenuItem value={null} style={{ display: 'none' }} data-testid="option-schedule-meeting-date-clear">
          <div style={{ textTransform: "none", color: '#8B90A0' }}>{'Choose preferred date'}</div>
        </MenuItem>
        {(dates ?? []).map((option, idx) => {
          return (
            <MenuItem value={option} key={idx} onClick={() => setSelectedDate(option)} data-testid={`option-schedule-meeting-date-${idx}`}>
              <div style={{ textTransform: "none" }}>{option?.dayString}</div>
            </MenuItem>
          )
        })}
      </Select>
      <Box className={styles.timezoneCaption}>
        Time zone: <span className={styles.value}>{TIMEZONE ?? ''} {TIMEZONE_OFFSET}</span>
      </Box>
    </FormControl>);
  };

  const renderAdditionalInfo = () => {
    return (<>
      <Box className={styles.info}><AccessTimeIcon className={styles.icon} /> {specialistUser.domiumCalendar?.[eventType]?.duration} min</Box>
      <Box className={styles.info}>{eventType === 'call' ? (<>
        <PhoneIcon className={styles.icon} /> Phone call
      </>) : (<>
        <VideoCallIcon className={styles.icon} /> Meeting
      </>)}</Box>
      <Box className={styles.info}>{specialistUser.domiumCalendar?.[eventType]?.description}</Box>
    </>);
  };

  const renderTimeSlots = () => {
    return (<Grid container item fullWidth>
      {!!selectedDate?.timeSlots?.length && (<>
        <Box sx={{ mb: 3 }} fullWidth className={styles.label}>Choose from available slots:</Box>
      </>)}
      <Grid container item direction={'row'} spacing={0} className={styles.timeSlotsWrapper} alignContent={'flex-start'} alignItems={'flex-start'} justifyContent={'flex-start'}>
        {(!dates?.length && !isProcessing) && (<Grid item xs={12} className={styles.label}>There are no available slots during nearest days</Grid>)}
        {(!isProcessing && !!dates?.length && !!selectedDate && !selectedDate?.timeSlots?.length) && (<Grid item xs={12} className={styles.label}>
          There are no available slots on the selected date
        </Grid>)}
        {!!selectedDate?.timeSlots?.length && (<>
          {selectedDate.timeSlots.map((slot, idx) => (
            <Grid item key={idx} className={styles.timeButtonWrapper}>
              <Button
                variant={selectedTime?.dateString === slot.dateString ? 'contained' : 'outlined'}
                data-testid={`button-schedule-meeting-slot-${idx}`}
                color={selectedTime?.dateString === slot.dateString ? 'primary' : 'secondary'}
                onClick={() => setSelectedTime(slot)}
                className={styles.smallBtn}
                fullWidth
                disabled={isProcessing}
              >
                {slot.buttonLabel}
              </Button>
            </Grid>
          ))}
        </>)}
      </Grid></Grid>);
  };

  const renderMobileContent = () => {
    return (<>
      <Grid container justifyContent="space-between" spacing={2}>
        <Grid item>
          <Box className={styles.avatarWrapper}>
            {specialistUser?.photo ? (
              <img className={styles.avatar} src={specialistUser.photo} alt="specialist's avatar" />
            ) : (
              <div className={styles.avatar}><PersonIcon style={{ fontSize: 48 }} /></div>
            )}
          </Box>
          <Box className={styles.specialistName}>{specialistUser.name} {specialistUser.surname}</Box>
        </Grid>
        <Grid
          item
          container
          className={styles.rightSection}
          justifyContent="flex-end"
          direction="column"
          alignContent="flex-end"
          alignItems="flex-end"
        >
          {renderAdditionalInfo()}
        </Grid>
      </Grid>
      <Box sx={{ mb: 3 }} />
      <Grid container justifyContent="space-between" spacing={4} direction={"column"}>
        {mode === REASON_MODE && (
          <Grid item className={styles.scheduleMeetingDialogWrapper}>
            {renderReasonInput()}
          </Grid>
        )}
        {mode === TIME_MODE && (<>
          <Grid item className={styles.scheduleMeetingDialogWrapper}>
            {renderDateSelector()}
          </Grid>
          {renderTimeSlots()}
        </>)}
      </Grid>
    </>);
  };

  const renderReasonInput = () => {
    return (<>
      <Box className={styles.inputLabel}>What's the reason for the call?</Box>
      <TextField
        style={{ maxWidth: '100%', backgroundColor: "white" }}
        inputProps={{ 'data-testid': 'input-schedule-meeting-reason' }}
        onChange={(event) => {
          setReasonToCall(event.target.value);
        }}
        value={reasonToCall}
        fullWidth
        placeholder="In a few words..."
        variant="outlined"
        maxLength="40"
      />
      <Box className={styles.reasonCaption}>
        Will appear as the meeting title
      </Box>
    </>);
  };


  return (
    <>
      <Dialog open={open} className={'ScheduleMeetingDialog'} onClose={() => { setSelectedTime(null); setSelectedDate(null); }} fullScreen={dimensions.isScreenLessSM}>
        <MuiDialogTitle>
          <Box className={`${styles.modalTitle} ${dimensions.isScreenLessMD ? styles.mobile : ''}`}>{mode === REASON_MODE ? 'Provide reason' : 'Choose time'}</Box>
        </MuiDialogTitle>
        <MuiDialogContent>
          {(activeUser?.id && specialistUser?.id) && (<>
            {dimensions?.isScreenBiggerSM ? (
              <Grid container justifyContent="space-between" className={`${styles.scheduleMeetingDialogWrapper}`}>
                <Grid
                  item
                  className={styles.leftSection}
                  container
                  justifyContent="flex-start"
                  direction="column"
                  alignContent="center"
                  alignItems="center"
                >
                  <Box className={styles.avatarWrapper}>
                    {specialistUser.photo ? (
                      <img className={styles.avatar} src={specialistUser.photo} alt="specialist's avatar" />
                    ) : (
                      <div className={styles.avatar}><PersonIcon style={{ fontSize: 96 }} /></div>
                    )}
                  </Box>
                  <Box className={styles.specialistName}>{specialistUser.name} {specialistUser.surname}</Box>
                  {renderAdditionalInfo()}
                </Grid>
                <Grid
                  item
                  className={styles.rightSection}
                  container
                  justifyContent="flex-start"
                  direction="column"
                  alignContent="flex-start"
                  alignItems="flex-start"
                  spacing={4}
                >
                  {mode === REASON_MODE && (
                    <Grid item className={styles.dateSelectorWrapper}>
                      {renderReasonInput()}
                    </Grid>
                  )}
                  {mode === TIME_MODE && (<>
                    <Grid item className={styles.dateSelectorWrapper}>
                      {renderDateSelector()}
                    </Grid>
                    {renderTimeSlots()}
                  </>)}
                </Grid>
              </Grid>
            ) : (
              <Box className={`${styles.scheduleMeetingDialogWrapper} ${styles.mobileContent}`}>
                {renderMobileContent()}
              </Box>
            )}
          </>)}
        </MuiDialogContent>
        <MuiDialogActions>
          {mode === REASON_MODE && (
            <Grid container justifyContent="space-between">
              <Button
                className={`${styles.actionButton} ${styles.secondary}`}
                data-testid="button-schedule-meeting-cancel"
                onClick={() => {
                  if (isFunc(noAction) && !isProcessing) {
                    setIsProcessing(true);
                    noAction();
                  }
                  setOpen(false);
                }
                }
                color="secondary"
                variant="outlined"
                type="button"
                disableElevation
                disabled={!!isProcessing}
              >
                CANCEL
              </Button>
              <Button
                className={`${styles.actionButton}`}
                type="button"
                variant="contained"
                color="primary"
                disabled={!!isProcessing || !reasonToCall}
                data-testid="button-schedule-meeting-continue"
                onClick={() => {
                  setMode(TIME_MODE);
                }}
              >
                CONTINUE
              </Button>
            </Grid>
          )}
          {mode === TIME_MODE && (
            <Grid container justifyContent="space-between">
              <Button
                className={`${styles.actionButton} ${styles.secondary}`}
                data-testid="button-schedule-meeting-back"
                onClick={() => {
                  setMode(REASON_MODE);
                }}
                color="secondary"
                variant="outlined"
                type="button"
                disableElevation
                disabled={!!isProcessing}
              >
                BACK
              </Button>
              <Button
                className={`${styles.actionButton}`}
                type="button"
                variant="contained"
                color="primary"
                disabled={!!isProcessing || !selectedTime}
                data-testid="button-schedule-meeting-complete"
                onClick={() => {
                  setIsProcessing(true);
                  localStorage.setItem('CALENDAR_REDIRECT_TIME', selectedTime.dateString);
                  dispatch(bookMeeting({
                    userId: specialistUser?.id,
                    customerUserId: !!selectedUser ? (selectedUser?.id ?? selectedUser?._id) : undefined,
                    eventType,
                    startTime: selectedTime.dateString,
                    reason: reasonToCall,
                    onSuccess: () => {
                      if (isFunc(yesAction)) yesAction(eventType, specialistUser?.id, selectedTime);
                      history.push('/scheduled');
                      setOpen(false);
                      setIsProcessing(false);
                    },
                    onError: (data) => {
                      displayErrorMessage(data, 'Unable to get available time slots');
                      setIsProcessing(false);
                    }
                  }));
                }}
              >
                COMPLETE
              </Button>
            </Grid>
          )}
        </MuiDialogActions>
      </Dialog>
    </>
  );
};

ScheduleMeetingModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func.isRequired,
  specialistUser: PropTypes.object,
  yesAction: PropTypes.func,
  noAction: PropTypes.func,
  closeOnSubmit: PropTypes.bool,
  eventType: PropTypes.string,
  reason: PropTypes.string
};

export default ScheduleMeetingModal;

